import {
  EDUCATION_CATEGORIES,
  EducationCategory,
  EducationListItemType,
  EducationSlideType,
  PLANNING_BASICS_CATEGORY,
  PLANNING_STRATEGIES_CATEGORY,
  PlanningStrategiesSlideType,
  STATE_INHERITANCE_TAX_CATEGORY,
  STATE_TAX_ITEMS,
  StateTaxEducationSlideType,
  TAX_TOPICS_CATEGORY,
} from './Education.constants';
import type { CustomSlide } from './EducationSlideOverrides/EducationSlideOverrides.context';

export function isActiveEducationSlideValid(
  activeSlide: string | null
): activeSlide is EducationListItemType {
  if (!activeSlide) return false;
  if (
    Object.values(EducationSlideType).includes(
      activeSlide as EducationSlideType
    )
  ) {
    return true;
  }
  if (
    Object.values(PlanningStrategiesSlideType).includes(
      activeSlide as PlanningStrategiesSlideType
    )
  ) {
    return true;
  }
  if (STATE_TAX_ITEMS.includes(activeSlide as StateTaxEducationSlideType)) {
    return true;
  }
  return false;
}

/**
 * Result type for groupCustomSlidesByCategory.
 * Contains an array for each category, the bundled state-tax slides, and
 * uncategorized custom slides.
 */
export type GroupedCustomSlides = {
  [K in EducationCategory]: CustomSlide[];
} & {
  uncategorized: CustomSlide[];
};

export function getCustomSlideEducationCategory(
  slide: CustomSlide
): EducationCategory | undefined {
  const category: string | undefined = slide.contentJSON?.educationCategory;
  if (
    category &&
    EDUCATION_CATEGORIES.includes(category as EducationCategory)
  ) {
    return category as EducationCategory;
  }
  return undefined;
}

/**
 * Groups custom slides by their education category.
 * Slides without a valid category are placed in 'uncategorized'.
 *
 * @param customSlidesMap - Map of slide identifiers to CustomSlide objects
 * @param filterHidden - Whether to filter out hidden slides (default: true)
 * @returns Grouped slides by category
 */
export function groupCustomSlidesByCategory(
  customSlidesMap: Record<string, CustomSlide>,
  filterHidden = true
): GroupedCustomSlides {
  const slides = Object.values(customSlidesMap);
  const visibleSlides = filterHidden
    ? slides.filter((slide) => !slide.isHidden)
    : slides;

  const grouped: GroupedCustomSlides = {
    [PLANNING_BASICS_CATEGORY]: [],
    [PLANNING_STRATEGIES_CATEGORY]: [],
    [TAX_TOPICS_CATEGORY]: [],
    [STATE_INHERITANCE_TAX_CATEGORY]: [],
    uncategorized: [],
  };

  visibleSlides.forEach((slide) => {
    const category = getCustomSlideEducationCategory(slide);
    if (category) {
      grouped[category].push(slide);
    } else {
      grouped.uncategorized.push(slide);
    }
  });

  return grouped;
}
